export type ThemeMode = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'cp.theme';

function isThemeMode(value: unknown): value is ThemeMode {
  return value === 'light' || value === 'dark' || value === 'system';
}

/**
 * Theme preference persistence.
 *
 * Deliberately `localStorage`, unlike the auth token which uses sessionStorage
 * (see @/features/auth/authStorage). The asymmetry is intentional: a theme is
 * not a credential, losing it is harmless, and it *should* survive closing the
 * browser. A bearer token should not.
 *
 * Every access is guarded because Safari's private mode throws on setItem, and
 * storage can be disabled entirely by policy.
 */
export const themeStorage = {
  get(): ThemeMode | null {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      return isThemeMode(stored) ? stored : null;
    } catch {
      return null;
    }
  },

  set(mode: ThemeMode): void {
    try {
      window.localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // Preference simply won't persist; not worth interrupting the user.
    }
  },
};
